/**
 * SharePoint Browser dialog.
 *
 * Two modes:
 *  - 'pick' (import): users tick files to import into the Library or as bid
 *    documents. Footer shows count + "Import N files" CTA.
 *  - 'save' (export): users navigate to a destination folder and provide a
 *    filename. Footer shows filename + conflict behavior + "Save" CTA.
 *
 * Both share site/drive/folder navigation with a breadcrumb. The store
 * (sharepoint-store.ts) owns all the async / state.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ArrowLeft,
  ChevronRight,
  Upload,
  ExternalLink,
  Folder,
  Loader2,
  RefreshCw,
  Search,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { FileTypeIcon } from '@/components/ui/file-type-icon'
import { cn } from '@/lib/utils'
import {
  useSharePointStore,
  type BrowserMode,
} from '@/stores/sharepoint-store'
import type { SharePointItem } from '@/lib/api'
import type { LibraryFolder } from '@/lib/schemas'

export interface SharePointStagedItem {
  itemId: string
  name: string
  size: number
  mimeType: string | null
}

/**
 * Pick-mode stage payload — handed to the parent via `onStage` when the
 * caller wants to defer the import API call (e.g. CreateBidDialog stages
 * items before the bid exists, then fires `importSharePointFiles` after
 * `createBid` succeeds with the new bidId).
 */
export interface SharePointStagedSelection {
  siteId: string
  driveId: string
  items: SharePointStagedItem[]
}

/**
 * Pick-mode constraints for callers that attach files somewhere other than the
 * Library / a bid — today, chat.
 *
 * Supplying this object **is** attach mode: the browse calls switch to the chat
 * items profile, the active-job dialog is suppressed (a background import must
 * not hijack an attach picker), the footer states the ephemerality of the
 * attachment, and ineligible rows are disabled with an inline reason.
 *
 * `validateItem` is owned by the caller — this component deliberately knows
 * nothing about chat's MIME/size rules.
 */
export interface SharePointPickConstraints {
  validateItem: (item: SharePointItem) => { valid: boolean; reason?: string }
  maxSelection?: number
  maxTotalBytes?: number
  allowAlreadyImported?: boolean
}

interface SharePointBrowserProps {
  open: boolean
  onClose: () => void
  mode: BrowserMode
  /** Overrides the dialog title. Defaults to the mode's import/save wording. */
  title?: string | undefined
  // Pick mode props
  libraryFolders?: LibraryFolder[] | undefined
  defaultLibraryFolderId?: string | null
  defaultBidId?: string | null
  /**
   * Pick mode only. If provided, the picker hands the selected items back via
   * this callback instead of calling the import API. The caller is responsible
   * for triggering `importSharePointFiles` later. Mutually exclusive with the
   * normal "import-now" flow — when set, the footer button is labelled
   * "Add to bid" and clicking it closes the dialog without contacting the API.
   */
  onStage?: (selection: SharePointStagedSelection) => void
  /** Pick mode only. Verb on the confirm button; defaults to "Import". */
  confirmLabel?: string | undefined
  /** Pick mode only. Presence switches the picker into attach mode. */
  pickConstraints?: SharePointPickConstraints | undefined
  // Save mode props
  defaultFilename?: string
  exportParams?: {
    artifactId: string
    templateId?: string | null
    sectionKey?: string | null
  }
  onSuccess?: () => void
}

export function SharePointBrowser({
  open,
  onClose,
  mode,
  title,
  libraryFolders,
  defaultLibraryFolderId,
  defaultBidId,
  onStage,
  confirmLabel,
  pickConstraints,
  defaultFilename,
  exportParams,
  onSuccess,
}: SharePointBrowserProps) {
  const {
    connection,
    sites,
    drives,
    items,
    breadcrumb,
    currentSiteId,
    currentDriveId,
    currentFolderId,
    isLoadingSites,
    isLoadingDrives,
    isLoadingItems,
    nextPage,
    selectedItems,
    saveFilename,
    saveConflictBehavior,
    activeJob,
    error,
    openBrowser,
    closeBrowser,
    fetchSites,
    resolveSiteByUrl,
    selectSite,
    selectDrive,
    navigateToFolder,
    navigateBackTo,
    loadNextPage,
    toggleItemSelection,
    clearSelection,
    importSelected,
    setSaveDestination,
    setSaveFilename,
    setSaveConflictBehavior,
    exportArtifact,
    clearActiveJob,
    clearError,
  } = useSharePointStore()

  const [sitesSearch, setSitesSearch] = useState('')
  const [destinationKind, setDestinationKind] = useState<'library' | 'bid'>(
    defaultBidId ? 'bid' : 'library'
  )
  const [destinationFolderId, setDestinationFolderId] = useState<string>(
    defaultLibraryFolderId ?? ''
  )

  /** Constraints present == attach mode. Nothing else toggles it. */
  const attachMode = pickConstraints != null
  const allowAlreadyImportedRows = pickConstraints?.allowAlreadyImported ?? false

  // Reset + load sites when opening
  useEffect(() => {
    if (open) {
      openBrowser(
        mode,
        pickConstraints
          ? {
              itemsProfile: 'chat',
              allowAlreadyImported: pickConstraints.allowAlreadyImported ?? false,
            }
          : undefined
      )
      void fetchSites()
      if (mode === 'save' && defaultFilename) {
        setSaveFilename(defaultFilename)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode])

  // When mode is save, push the current folder selection into the store on every change
  useEffect(() => {
    if (mode !== 'save' || !currentDriveId || !currentFolderId) return
    const folderPath = breadcrumb.map((c) => c.name).join(' / ')
    setSaveDestination(currentDriveId, currentFolderId, folderPath)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, currentDriveId, currentFolderId, breadcrumb.length])

  // After active job completes, fire onSuccess exactly once per job.
  //
  // Watching activeJob + onSuccess directly causes an infinite render loop:
  //   - activeJob's object reference changes on every poll tick.
  //   - The parent (LibraryToolbar / ArtifactDetailPage) passes onSuccess as
  //     an inline closure, so its reference is new on every parent render —
  //     and onSuccess typically triggers a parent re-render via the library
  //     refresh, recursing infinitely.
  //
  // Fix: depend only on the primitive jobId + status, and use a ref to
  // guarantee onSuccess fires once per completed job ID.
  const jobId = activeJob?.jobId
  const jobStatus = activeJob?.status.status
  const lastFiredJobIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!jobId) {
      lastFiredJobIdRef.current = null
      return
    }
    if (jobStatus === 'completed' && lastFiredJobIdRef.current !== jobId) {
      lastFiredJobIdRef.current = jobId
      onSuccess?.()
    }
    // onSuccess intentionally omitted from deps — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, jobStatus])

  const showSites = !currentSiteId
  const showDrives = currentSiteId && !currentDriveId
  const showItems = currentSiteId && currentDriveId

  const filteredSites = useMemo(() => {
    if (!sitesSearch.trim()) return sites
    const q = sitesSearch.toLowerCase()
    return sites.filter((s) => s.displayName.toLowerCase().includes(q))
  }, [sites, sitesSearch])

  const selectedCount = selectedItems.size

  const selectionTotalBytes = useMemo(
    () => Array.from(selectedItems.values()).reduce((sum, it) => sum + (it.size ?? 0), 0),
    [selectedItems]
  )

  /** Non-null blocks the confirm button and is shown beside the count. */
  const overCapReason = !pickConstraints
    ? null
    : pickConstraints.maxSelection !== undefined && selectedCount > pickConstraints.maxSelection
      ? `You can attach up to ${pickConstraints.maxSelection} more file${pickConstraints.maxSelection === 1 ? '' : 's'}`
      : pickConstraints.maxTotalBytes !== undefined && selectionTotalBytes > pickConstraints.maxTotalBytes
        ? `Selection exceeds the space left in this chat (${formatSize(pickConstraints.maxTotalBytes)})`
        : null

  /** Null when the item can be picked. Folders are never validated. */
  const getIneligibleReason = (item: SharePointItem): string | null => {
    if (!pickConstraints || item.isFolder) return null
    const result = pickConstraints.validateItem(item)
    if (result.valid) return null
    return result.reason ?? "This file can't be attached"
  }

  const filenameInvalid =
    mode === 'save' &&
    (!saveFilename.trim() ||
      /[\\/:*?"<>|]/.test(saveFilename) ||
      !saveFilename.toLowerCase().endsWith('.docx'))

  const handleClose = () => {
    closeBrowser()
    clearActiveJob()
    clearError()
    onClose()
  }

  const handleImport = async () => {
    if (selectedCount === 0) return

    // Attach mode's confirm button drops the destination check, so without a
    // stage callback the flow below would run a REAL Library import under a
    // footer promising "this conversation only". Refuse instead.
    if (attachMode && !onStage) return

    // Stage-only mode: hand the selection back to the caller without calling
    // the import API. Used by CreateBidDialog where the bid doesn't exist yet.
    if (onStage) {
      if (!currentSiteId || !currentDriveId) return
      const items = Array.from(selectedItems.values()).map((it) => ({
        itemId: it.id,
        name: it.name,
        size: it.size ?? 0,
        mimeType: it.mimeType ?? null,
      }))
      onStage({ siteId: currentSiteId, driveId: currentDriveId, items })
      handleClose()
      return
    }

    const destination =
      destinationKind === 'bid' && defaultBidId
        ? { type: 'bid' as const, bidId: defaultBidId }
        : { type: 'library' as const, folderId: destinationFolderId }
    if (destination.type === 'library' && !destination.folderId) return
    await importSelected(destination)
  }

  const handleSave = async () => {
    if (!exportParams || filenameInvalid) return
    await exportArtifact(exportParams)
  }

  const handleReset = () => {
    clearSelection()
    void fetchSites()
  }

  // ===================
  // Active-job UI
  // ===================
  // Attach mode never shows it: a background import/export the user already
  // backgrounded must not hijack a picker opened to attach files to a chat.
  if (activeJob && !attachMode) {
    const status = activeJob.status
    const isImport = activeJob.kind === 'import'
    const isComplete = status.status === 'completed'
    const isFailed = status.status === 'failed'
    return (
      <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>
              {isImport
                ? isComplete ? 'Import complete' : isFailed ? 'Import failed' : 'Importing…'
                : isComplete ? 'Saved to SharePoint' : isFailed ? 'Save failed' : 'Saving to SharePoint…'}
            </DialogTitle>
          </DialogHeader>
          {!isComplete && !isFailed && (
            <div className="flex items-center gap-3 text-sm text-[hsl(var(--muted-foreground))]">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isImport
                ? `Working through ${status.itemsTotal ?? '?'} files…`
                : 'Rendering + uploading. Typically 15–30 seconds.'}
            </div>
          )}
          {isImport && (isComplete || isFailed) && (
            <div className="text-sm text-[hsl(var(--muted-foreground))]">
              {status.itemsDone ?? 0} imported · {status.itemsSkipped ?? 0} skipped · {status.itemsFailed ?? 0} failed
            </div>
          )}
          {!isImport && isComplete && status.itemWebUrl && (
            <a
              href={status.itemWebUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-[hsl(var(--brand))] hover:underline"
            >
              Open in SharePoint <ExternalLink className="h-3 w-3" />
            </a>
          )}
          {isFailed && status.errorMessage && (
            <div className="text-sm text-[hsl(var(--destructive))]">{status.errorMessage}</div>
          )}
          <div className="flex justify-end gap-2">
            {isFailed && (
              <Button variant="outline" size="sm" onClick={() => clearActiveJob()}>
                Try again
              </Button>
            )}
            <Button size="sm" onClick={handleClose}>
              {isComplete || isFailed ? 'Close' : 'Run in background'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // ===================
  // Browser UI
  // ===================
  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="max-w-3xl max-h-[80vh] flex flex-col gap-0 p-0 overflow-hidden"
        aria-describedby={undefined}
      >
        <DialogHeader className="px-6 py-4 border-b border-[hsl(var(--border))]">
          <DialogTitle>
            {title ?? (mode === 'pick' ? 'Import from SharePoint' : 'Save to SharePoint')}
          </DialogTitle>
          {connection && (
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              Connected as {connection.userPrincipalName}
            </p>
          )}
        </DialogHeader>

        {error && (
          <div role="alert" className="px-6 py-2 bg-[hsl(var(--destructive)/0.08)] text-sm text-[hsl(var(--destructive))]">
            {error}
          </div>
        )}

        {/* Breadcrumb */}
        {(currentSiteId || currentDriveId) && (
          <nav
            aria-label="Folder breadcrumb"
            className="px-6 py-2 border-b border-[hsl(var(--border))] flex items-center gap-1 text-sm overflow-x-auto"
          >
            <button
              className="flex items-center gap-1 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
              onClick={() => openBrowser(mode)}
            >
              <ArrowLeft className="h-3 w-3" />
              Sites
            </button>
            {currentSiteId && (
              <>
                <ChevronRight className="h-3 w-3 text-[hsl(var(--muted-foreground))]" />
                <button
                  className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                  onClick={() => { void selectSite(currentSiteId) }}
                >
                  Libraries
                </button>
              </>
            )}
            {breadcrumb.map((crumb, i) => (
              <span key={crumb.id} className="flex items-center gap-1">
                <ChevronRight className="h-3 w-3 text-[hsl(var(--muted-foreground))]" />
                <button
                  aria-current={i === breadcrumb.length - 1 ? 'page' : undefined}
                  onClick={() => { void navigateBackTo(i) }}
                  className={cn(
                    'hover:text-[hsl(var(--foreground))]',
                    i === breadcrumb.length - 1
                      ? 'text-[hsl(var(--foreground))] font-medium'
                      : 'text-[hsl(var(--muted-foreground))]'
                  )}
                >
                  {crumb.name}
                </button>
              </span>
            ))}
          </nav>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-3" aria-live="polite">
          {showSites && (
            <SitesList
              sites={filteredSites}
              search={sitesSearch}
              onSearch={setSitesSearch}
              isLoading={isLoadingSites}
              onSelect={(siteId) => { void selectSite(siteId) }}
              onResolveByUrl={(url) => { void resolveSiteByUrl(url) }}
            />
          )}
          {showDrives && (
            <DrivesList
              drives={drives}
              isLoading={isLoadingDrives}
              onSelect={(d) => { void selectDrive(d.id, d.name) }}
            />
          )}
          {showItems && (
            <ItemsList
              items={items}
              mode={mode}
              selectedItems={selectedItems}
              isLoading={isLoadingItems}
              nextPage={nextPage}
              allowAlreadyImportedRows={allowAlreadyImportedRows}
              getIneligibleReason={pickConstraints ? getIneligibleReason : undefined}
              onToggle={toggleItemSelection}
              onNavigate={(item) => { void navigateToFolder(item.id, item.name) }}
              onLoadMore={() => { void loadNextPage() }}
            />
          )}
        </div>

        {/* Footer — attach mode keeps it pinned from the first screen so the
            "this conversation only" promise is visible before anything is picked. */}
        {(showItems || attachMode) && mode === 'pick' && (
          <PickFooter
            selectedCount={selectedCount}
            libraryFolders={libraryFolders}
            destinationKind={destinationKind}
            destinationFolderId={destinationFolderId}
            onDestinationKindChange={setDestinationKind}
            onDestinationFolderChange={setDestinationFolderId}
            hasBid={!!defaultBidId}
            stageMode={!!onStage}
            attachMode={attachMode}
            confirmLabel={confirmLabel}
            overCapReason={overCapReason}
            onReset={handleReset}
            onImport={() => { void handleImport() }}
          />
        )}
        {showItems && mode === 'save' && (
          <SaveFooter
            filename={saveFilename}
            conflictBehavior={saveConflictBehavior}
            filenameInvalid={filenameInvalid}
            destinationPath={breadcrumb.map((c) => c.name).join(' / ')}
            onFilenameChange={setSaveFilename}
            onConflictBehaviorChange={setSaveConflictBehavior}
            onSave={() => { void handleSave() }}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

// ===================
// Sub-components
// ===================

interface SitesListProps {
  sites: Array<{ id: string; displayName: string; webUrl: string; description?: string }>
  search: string
  onSearch: (q: string) => void
  isLoading: boolean
  onSelect: (siteId: string) => void
  onResolveByUrl: (url: string) => void
}

function SitesList({ sites, search, onSearch, isLoading, onSelect, onResolveByUrl }: SitesListProps) {
  const [urlInput, setUrlInput] = useState('')
  const [showUrlInput, setShowUrlInput] = useState(false)

  const submitUrl = () => {
    const trimmed = urlInput.trim()
    if (!trimmed) return
    onResolveByUrl(trimmed)
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
        <Input
          placeholder="Search sites…"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          className="pl-9 bg-[hsl(var(--muted))]"
        />
      </div>

      {/* Don't see your site? Paste-URL escape hatch — works around the 24–48h
          search-indexing delay on new tenants and Team-site visibility gaps. */}
      {!showUrlInput ? (
        <button
          type="button"
          onClick={() => setShowUrlInput(true)}
          className="text-xs text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] underline"
        >
          Don't see your site? Enter SharePoint URL
        </button>
      ) : (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <Input
              placeholder="https://contoso.sharepoint.com/sites/Engineering"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitUrl() }}
              className="bg-[hsl(var(--muted))] flex-1"
              autoFocus
            />
            <Button size="sm" onClick={submitUrl} disabled={!urlInput.trim()}>
              Open
            </Button>
          </div>
          <p className="text-[10px] text-[hsl(var(--muted-foreground))]">
            Paste any SharePoint site URL — works for Team sites and brand-new sites that haven't shown up in search yet.
          </p>
        </div>
      )}
      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
        </div>
      ) : sites.length === 0 ? (
        <EmptyState message="No SharePoint sites visible to your account." />
      ) : (
        <ul className="border border-[hsl(var(--border))] rounded-lg divide-y divide-[hsl(var(--border))] bg-[hsl(var(--card))]">
          {sites.map((site) => (
            <li key={site.id}>
              <button
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[hsl(var(--accent)/0.3)]"
                onClick={() => onSelect(site.id)}
              >
                <Folder className="h-4 w-4 text-[hsl(var(--muted-foreground))] flex-shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm truncate">{site.displayName}</span>
                  <span className="block text-xs text-[hsl(var(--muted-foreground))] truncate">{site.webUrl}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

interface DrivesListProps {
  drives: Array<{ id: string; name: string; webUrl: string }>
  isLoading: boolean
  onSelect: (drive: { id: string; name: string }) => void
}

function DrivesList({ drives, isLoading, onSelect }: DrivesListProps) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[0, 1].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    )
  }
  if (drives.length === 0) {
    return <EmptyState message="No document libraries on this site." />
  }
  return (
    <ul className="border border-[hsl(var(--border))] rounded-lg divide-y divide-[hsl(var(--border))] bg-[hsl(var(--card))]">
      {drives.map((drive) => (
        <li key={drive.id}>
          <button
            className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[hsl(var(--accent)/0.3)]"
            onClick={() => onSelect(drive)}
          >
            <Folder className="h-4 w-4 text-[hsl(var(--muted-foreground))] flex-shrink-0" />
            <span className="text-sm">{drive.name}</span>
          </button>
        </li>
      ))}
    </ul>
  )
}

interface ItemsListProps {
  items: SharePointItem[]
  mode: BrowserMode
  selectedItems: Map<string, SharePointItem>
  isLoading: boolean
  nextPage: string | null
  /** Attach flows re-pick library files, so "already imported" stops blocking
   *  the row and the badge is dropped — it would read as a warning. */
  allowAlreadyImportedRows: boolean
  /** Attach mode only. Non-null disables the row and replaces its subtext. */
  getIneligibleReason?: ((item: SharePointItem) => string | null) | undefined
  onToggle: (item: SharePointItem) => void
  onNavigate: (item: { id: string; name: string }) => void
  onLoadMore: () => void
}

function ItemsList({
  items,
  mode,
  selectedItems,
  isLoading,
  nextPage,
  allowAlreadyImportedRows,
  getIneligibleReason,
  onToggle,
  onNavigate,
  onLoadMore,
}: ItemsListProps) {
  if (isLoading && items.length === 0) {
    return (
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
      </div>
    )
  }
  if (items.length === 0) {
    return <EmptyState message="This folder is empty." />
  }
  return (
    <div>
      <ul
        role="list"
        className="border border-[hsl(var(--border))] rounded-lg divide-y divide-[hsl(var(--border))] bg-[hsl(var(--card))]"
      >
        {items.map((item) => {
          const isSelected = selectedItems.has(item.id)
          const reason = getIneligibleReason?.(item) ?? null
          const importBlocked = item.alreadyImported && !allowAlreadyImportedRows
          const blocked = reason != null || importBlocked
          return (
            <li
              key={item.id}
              role="row"
              aria-selected={mode === 'pick' ? isSelected : undefined}
              className={cn(
                'flex items-center gap-3 px-3 py-2',
                !item.isFolder && blocked && 'opacity-60'
              )}
            >
              {mode === 'pick' && !item.isFolder && (
                <input
                  type="checkbox"
                  aria-label={item.name}
                  disabled={blocked}
                  checked={isSelected}
                  onChange={() => onToggle(item)}
                  className="h-4 w-4"
                />
              )}
              {(mode === 'save' || item.isFolder) && (
                <span className="w-4 inline-block" />
              )}
              {item.isFolder ? (
                <Folder className="h-4 w-4 text-[hsl(var(--muted-foreground))] flex-shrink-0" />
              ) : (
                /* Graph leaves mimeType null for anything it can't type, so the
                   filename is the fallback — see getFileKind. */
                <FileTypeIcon
                  mimeType={item.mimeType}
                  fileName={item.name}
                  size={20}
                  className="flex-shrink-0"
                />
              )}
              <button
                disabled={!item.isFolder && (mode === 'save' || blocked)}
                onClick={() => {
                  if (item.isFolder) onNavigate(item)
                  else if (mode === 'pick' && !blocked) onToggle(item)
                }}
                className="flex-1 min-w-0 text-left"
              >
                <span className="block text-sm truncate">{item.name}</span>
                {!item.isFolder && (
                  <span className="block text-xs text-[hsl(var(--muted-foreground))]">
                    {/* The reason replaces the metadata line rather than sitting in a
                        tooltip — a disabled row can't be hovered to explain itself. */}
                    {reason ?? `${formatSize(item.size)} · ${formatDate(item.lastModifiedDateTime)}`}
                  </span>
                )}
              </button>
              {!item.isFolder && item.alreadyImported && !allowAlreadyImportedRows && (
                <span className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1">
                  ✓ Imported
                </span>
              )}
            </li>
          )
        })}
      </ul>
      {nextPage && (
        <div className="pt-3 flex justify-center">
          <Button variant="ghost" size="sm" onClick={onLoadMore} disabled={isLoading}>
            {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  )
}

interface PickFooterProps {
  selectedCount: number
  libraryFolders?: LibraryFolder[] | undefined
  destinationKind: 'library' | 'bid'
  destinationFolderId: string
  onDestinationKindChange: (k: 'library' | 'bid') => void
  onDestinationFolderChange: (id: string) => void
  hasBid: boolean
  /** When true, the action button reads "Add to bid" and the destination
   *  helper text reflects deferred-import behaviour. */
  stageMode: boolean
  /** Swaps the destination picker for the ephemerality line and applies the caps. */
  attachMode: boolean
  confirmLabel?: string | undefined
  /** Non-null blocks confirm and is shown beside the selected count. */
  overCapReason: string | null
  onReset: () => void
  onImport: () => void
}

function PickFooter({
  selectedCount,
  libraryFolders,
  destinationFolderId,
  onDestinationFolderChange,
  hasBid,
  stageMode,
  attachMode,
  confirmLabel,
  overCapReason,
  destinationKind,
  onDestinationKindChange,
  onReset,
  onImport,
}: PickFooterProps) {
  const noLibraryFolders = !libraryFolders || libraryFolders.length === 0
  const countWord = selectedCount === 1 ? 'file' : 'files'
  const buttonLabel = attachMode
    ? // Drop the empty count segment rather than leaving a double space.
      [confirmLabel ?? 'Import', selectedCount || '', countWord].filter((p) => p !== '').join(' ')
    : stageMode
      ? `Add ${selectedCount || ''} ${countWord} to bid`
      : `Import ${selectedCount || ''} ${countWord}`
  return (
    <div className="border-t border-[hsl(var(--border))] px-6 py-3 flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {attachMode ? (
          <span className="text-sm text-[hsl(var(--muted-foreground))]">
            Attached for this conversation only — it won't be added to your documents.
          </span>
        ) : stageMode ? (
          <span className="text-sm text-[hsl(var(--muted-foreground))]">
            Files will import once the bid is created
          </span>
        ) : hasBid ? (
          <span className="text-sm text-[hsl(var(--muted-foreground))]">
            Attaching to current bid
          </span>
        ) : (
          <>
            <span className="text-sm text-[hsl(var(--muted-foreground))]">Import to:</span>
            <Select value={destinationFolderId} onValueChange={onDestinationFolderChange}>
              <SelectTrigger className="h-9 w-[220px] bg-[hsl(var(--card))]">
                <SelectValue placeholder="Select a folder…" />
              </SelectTrigger>
              <SelectContent>
                {noLibraryFolders && (
                  <SelectItem value="__none__" disabled>
                    No library folders yet
                  </SelectItem>
                )}
                {libraryFolders?.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
      </div>
      {overCapReason && (
        <span className="text-xs text-[hsl(var(--destructive))]">{overCapReason}</span>
      )}
      <span className="text-sm text-[hsl(var(--muted-foreground))]">
        {selectedCount} file{selectedCount === 1 ? '' : 's'} selected
      </span>
      <Button variant="ghost" size="sm" onClick={onReset} aria-label="Clear selection">
        <RefreshCw className="h-3 w-3" />
      </Button>
      <Button
        size="sm"
        disabled={
          selectedCount === 0 ||
          (attachMode
            ? overCapReason != null
            : !stageMode && !hasBid && !destinationFolderId)
        }
        onClick={onImport}
      >
        <Upload className="h-4 w-4 mr-1.5" />
        {buttonLabel}
      </Button>
      {/* Mark destinationKind to keep linter happy when we don't render the toggle */}
      <span hidden>{destinationKind}</span>
      <span hidden onClick={() => onDestinationKindChange('library')} />
    </div>
  )
}

interface SaveFooterProps {
  filename: string
  conflictBehavior: 'rename' | 'replace' | 'fail'
  filenameInvalid: boolean
  destinationPath: string
  onFilenameChange: (n: string) => void
  onConflictBehaviorChange: (b: 'rename' | 'replace' | 'fail') => void
  onSave: () => void
}

function SaveFooter({
  filename,
  conflictBehavior,
  filenameInvalid,
  destinationPath,
  onFilenameChange,
  onConflictBehaviorChange,
  onSave,
}: SaveFooterProps) {
  return (
    <div className="border-t border-[hsl(var(--border))] px-6 py-3 space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-sm text-[hsl(var(--muted-foreground))]">Save to:</span>
        <span className="text-sm font-medium truncate">{destinationPath || 'Pick a folder above'}</span>
      </div>
      <div>
        <label className="text-xs text-[hsl(var(--muted-foreground))]">Filename</label>
        <Input
          value={filename}
          onChange={(e) => onFilenameChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !filenameInvalid) onSave()
          }}
          aria-invalid={filenameInvalid}
          className="bg-[hsl(var(--muted))]"
          placeholder="response.docx"
        />
        {filenameInvalid && filename.length > 0 && (
          <p className="text-xs text-[hsl(var(--destructive))] mt-1">
            Filename must end in .docx and not contain \ / : * ? " &lt; &gt; |
          </p>
        )}
      </div>
      <div className="flex items-center gap-4">
        <label className="text-xs text-[hsl(var(--muted-foreground))]">If a file with that name already exists:</label>
        <label className="flex items-center gap-1 text-sm">
          <input
            type="radio"
            checked={conflictBehavior === 'rename'}
            onChange={() => onConflictBehaviorChange('rename')}
          />
          Add as new file
        </label>
        <label className="flex items-center gap-1 text-sm">
          <input
            type="radio"
            checked={conflictBehavior === 'replace'}
            onChange={() => onConflictBehaviorChange('replace')}
          />
          Replace
        </label>
      </div>
      <div className="flex justify-end">
        <Button size="sm" disabled={filenameInvalid || !destinationPath} onClick={onSave}>
          Save to SharePoint
        </Button>
      </div>
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="py-12 text-center text-sm text-[hsl(var(--muted-foreground))]">
      {message}
    </div>
  )
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  } catch {
    return iso
  }
}

